import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { checkUserExists, getUserKeyFromPhone, loginApple } from 'actions/AuthActions'
import { trackClientEvent } from 'analytics/analytics'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import noop from 'lib/function/noop'
import type { LoginCaptchaHandle } from './LoginCaptcha'
import useSignUpEmailCheck from './useSignUpEmailCheck'

interface LoginForm {
  email?: string
  phoneNumber?: App.PhoneNumber
}

interface Options {
  onContinue: (mode: App.UiAccountModalMode, userId: string, appleData?: App.AppleLoginData) => void
  applePrivateEmailData: App.AppleLoginData | null
  setApplePrivateEmailData: (data: App.AppleLoginData | null) => void
  trackEvents?: boolean
  defaultEmail?: string
  autoContinueEmail?: boolean
  onAutoContinue?: () => void
  loginMethod?: 'phone'
}

/** Shared authentication behaviour for the standard login step and offer sign-up content. */
function useAccountAccessLoginStart({
  onContinue,
  applePrivateEmailData,
  setApplePrivateEmailData,
  trackEvents = true,
  defaultEmail,
  autoContinueEmail,
  onAutoContinue = noop,
  loginMethod,
}: Options) {
  const dispatch = useAppDispatch()
  const [userId, setUserId] = useState('')
  // Resubmitting a cached account must re-run the eligibility check.
  const [emailSubmitCount, setEmailSubmitCount] = useState(0)
  const processing = useAppSelector(state => state.auth.processing)
  const user = useAppSelector(state => state.auth.users[userId])
  const { startEmailCheck, isCheckingEmail, rejectionMessage } = useSignUpEmailCheck(emailSubmitCount)
  const reRef = useRef<LoginCaptchaHandle>(null)
  const autoContinued = useRef(false)
  const hasTrackedImpression = useRef(false)
  // Act on each Apple sign-in once; the effect below re-runs when its login settles
  const appleSignInHandled = useRef<App.AppleLoginData | null>(null)

  const onPrivateAppleEmailLogin = useCallback((appleData: App.AppleLoginData, email: string) => {
    setUserId(email)
    setApplePrivateEmailData(appleData)
    dispatch(checkUserExists({ email }, reRef.current ?? undefined))
  }, [dispatch, setApplePrivateEmailData])

  const onCheckEmail = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const loginData = formToObject<LoginForm>(event.currentTarget)
    if (trackEvents) {
      trackClientEvent({
        subject: 'sign_up_appear_continue',
        action: 'interaction',
        category: loginData.phoneNumber ? 'phone' : 'email',
        type: 'operational',
      })
    }
    setUserId(loginData.phoneNumber ? getUserKeyFromPhone(loginData.phoneNumber) : loginData.email ?? '')
    setApplePrivateEmailData(null)
    setEmailSubmitCount(count => count + 1)
    dispatch(checkUserExists(loginData, reRef.current ?? undefined))
  }, [dispatch, setApplePrivateEmailData, trackEvents])

  useEffect(() => {
    if (processing || !user) return
    if (applePrivateEmailData && appleSignInHandled.current === applePrivateEmailData) return
    const resolvedUserId = user.id ?? userId

    if (user.exists) {
      if (applePrivateEmailData) {
        appleSignInHandled.current = applePrivateEmailData
        dispatch(loginApple(applePrivateEmailData))
      } else {
        onContinue(user.maskedPhone ? 'loginPasswordOTP' : 'loginPassword', resolvedUserId)
      }
      return
    }
    if (applePrivateEmailData && user.id?.endsWith('@privaterelay.appleid.com')) {
      appleSignInHandled.current = applePrivateEmailData
      onContinue('applePrivateEmail', resolvedUserId, applePrivateEmailData)
      return
    }
    if (!user.email) {
      onContinue('join', resolvedUserId)
      return
    }
    return startEmailCheck(user, () => onContinue('join', resolvedUserId))
    // Continue only for a new account answer, submit or Apple sign-in, not a parent callback change.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userId, processing, emailSubmitCount, applePrivateEmailData])

  useEffect(() => {
    if (trackEvents && !hasTrackedImpression.current) {
      hasTrackedImpression.current = true
      trackClientEvent({
        subject: 'sign_up_appear',
        action: 'impression',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [trackEvents])

  useEffect(() => {
    if (!autoContinueEmail || !defaultEmail || loginMethod === 'phone' || autoContinued.current) return
    autoContinued.current = true
    onAutoContinue()
    setUserId(defaultEmail)
    dispatch(checkUserExists({ email: defaultEmail }, reRef.current ?? undefined))
  }, [autoContinueEmail, defaultEmail, dispatch, loginMethod, onAutoContinue])

  return { processing, isCheckingEmail, rejectionMessage, reRef, onCheckEmail, onPrivateAppleEmailLogin }
}

export default useAccountAccessLoginStart
